import {
  assignPendingServiceRequestAssignment,
  usePendingServiceRequestAssignments,
} from '@commenda/api';
import {
  Avatar,
  Badge,
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  FlagComponent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useState } from 'react';

/**
 * Prompt shown after login when the user has a paid website self-serve
 * incorporation purchase that isn't attached to a company yet.
 */
export const PendingServiceRequestModal = () => {
  const { pendingAssignments, companies, isLoading, mutate } =
    usePendingServiceRequestAssignments();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pending = pendingAssignments[0];
  const hasCompanies = companies.length > 0;

  if (isLoading || !pending) return null;

  const handleAssign = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await assignPendingServiceRequestAssignment({
        pendingAssignmentId: pending.id,
        ...(hasCompanies ? { companyId: Number(selectedCompanyId) } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.message ?? 'Could not assign the purchase. Please try again.'
        );
      }
      await mutate();
      setSelectedCompanyId('');
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Could not assign the purchase. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="max-w-lg p-3xl flex flex-col gap-2xl"
        hideCloseIcon
        preventCloseOnOutsideClick
        preventCloseOnEscapeKeyDown
      >
        <DialogTitle className="txt-large-bold text-text-primary-900 text-left">
          Almost there
        </DialogTitle>

        <div className="flex items-center gap-lg rounded-lg bg-bg-secondary p-xl">
          <FlagComponent
            _className="size-10 shrink-0"
            country={pending.country as CountryCode}
            countryName={pending.countryName}
          />
          <div className="flex flex-col flex-1 min-w-0">
            <p className="txt-medium-bold text-text-primary-900">
              {pending.countryName} — Managed Entity
            </p>
            <p className="txt-small-regular text-text-tertiary">
              Incorporation and ongoing compliance, fully managed
            </p>
          </div>
          <Badge className="uppercase tracking-wide" size="md" variant="blue">
            Purchased
          </Badge>
        </div>

        {hasCompanies ? (
          <div className="flex flex-col gap-md">
            <span className="txt-small-medium text-text-primary-900">
              Company
            </span>
            <Select
              onValueChange={setSelectedCompanyId}
              value={selectedCompanyId}
            >
              <SelectTrigger className="h-12 px-lg">
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={String(company.id)}>
                    <div className="flex items-center gap-md">
                      <Avatar
                        name={company.displayName ?? `Company ${company.id}`}
                        size="xs"
                      />
                      <span className="txt-medium-regular text-text-primary-900">
                        {company.displayName ?? `Company #${company.id}`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="txt-small-regular text-text-tertiary">
              Pick the company where we should set this up. Your incorporation
              and all ongoing compliance work will live under this company's
              dashboard.
            </p>
          </div>
        ) : (
          <p className="txt-small-regular text-text-tertiary">
            You don't have a company on Commenda yet — we'll set one up for you
            and attach this incorporation to it.
          </p>
        )}

        {submitError && (
          <p className="txt-small-regular text-utility-error-700">
            {submitError}
          </p>
        )}

        <DialogFooter className="border-t border-border-secondary pt-2xl gap-md">
          <Button
            disabled={isSubmitting || (hasCompanies && !selectedCompanyId)}
            onClick={() => {
              handleAssign().catch(() => undefined);
            }}
          >
            {isSubmitting ? 'Confirming…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
